import { TextFieldProps } from "@mui/material/TextField";
import { DatePicker, DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { ERROR_BOUNDARY_MESSAGE } from "@/constants";
import { stripTimezoneOffset } from "@/helpers";

export type FormDateValue = null | string;

export type FormDateProps<TFormValues extends FieldValues> = Omit<
  Partial<DatePickerProps<dayjs.Dayjs>>,
  "defaultValue"
> & {
  name: Path<TFormValues>;
  required?: TextFieldProps["required"];
  label?: TextFieldProps["label"];
  helperText?: TextFieldProps["helperText"];
};

export function FormDate<TFormValues extends FieldValues>({
  name,
  label = "",
  helperText = "",
  required = false,
  openTo = "year",
  views = ["year", "month", "day"],
  slotProps = {},
  ...props
}: FormDateProps<TFormValues>) {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<TFormValues>();

  const inputFormat =
    views.includes("year") && views.includes("month") && views.includes("day")
      ? "MM/DD/YYYY"
      : views.includes("year") && views.includes("month")
      ? "MM/YYYY"
      : "YYYY";

  return (
    <Sentry.ErrorBoundary fallback={<div>{ERROR_BOUNDARY_MESSAGE.component}</div>}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DatePicker
            value={!field.value || field.value?.includes("2600-") ? null : dayjs(stripTimezoneOffset(field.value))}
            openTo={openTo}
            views={views}
            format={inputFormat}
            slotProps={{
              ...slotProps,
              textField: {
                variant: "outlined",
                name: field.name,
                error: !!(errors && errors[name]),
                helperText: errors && errors[name] ? (errors[name]?.message as string) : helperText,
                label,
                required,
                fullWidth: true,
                ...slotProps.textField,
              },
            }}
            onChange={(value: dayjs.Dayjs | null) => {
              if (!value) {
                setValue(name, null as PathValue<TFormValues, Path<TFormValues>>);
              } else {
                const isDateInvalid = !value.isValid();

                if (!isDateInvalid) {
                  clearErrors(name);
                }

                setValue(
                  name,
                  (isDateInvalid ? "Invalid" : dayjs(value).format("YYYY-MM-DD")) as PathValue<
                    TFormValues,
                    Path<TFormValues>
                  >
                );
              }
            }}
            {...props}
          />
        )}
      />
    </Sentry.ErrorBoundary>
  );
}
